import { Banner, Chart, Select } from '@cloudflare/kumo';
import * as echarts from 'echarts';
import { useEffect, useMemo, useState, type ComponentProps } from 'react';
import { DataTable } from '../../components/ui/DataTable';
import { PageLayout } from '../../components/ui/PageLayout';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { Detail, Status, type Turn } from './AgentTurnsPanel';
import { usd } from './agentFormat';
import { SkeletonText } from '../../components/ui/Skeleton';

type ChartOptions = ComponentProps<typeof Chart>['options'];
interface Day {
  day: string; turns: number; tokensIn: number; tokensOut: number; tools: number; p95Ms: number; p95FirstMs: number;
  errors: number; fallbacks: number; costMicroUsd: number; toolErrors: number;
}
type Keyed = Day & { key: string };
interface Overview {
  agents: Array<{ id: string; name: string }>;
  usage: { available: true; days: Day[]; totals: Omit<Day, 'day'>; byAgent: Keyed[]; byModel: Keyed[] } | { available: false; message: string };
  turns: Array<Turn & { agentId: string | null }>;
}

const PERIODS = [
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '90', label: 'Últimos 90 dias' },
];
const FILTERS = [
  { value: '', label: 'Todos os turnos' },
  { value: 'error', label: 'Com erro' },
  { value: 'fallback', label: 'Modelo de reserva' },
  { value: 'tool_error', label: 'Ferramenta falhou' },
];
const n = (v: number) => v.toLocaleString('pt-BR');
const pct = (part: number, total: number) => (total ? `${(Math.round((part / total) * 1000) / 10).toLocaleString('pt-BR')}%` : '—');
const secs = (ms: number) => (ms ? `${(ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s` : '—');
const dayLabel = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

function Tile({ label, value, hint, testId }: { label: string; value: string; hint?: string; testId?: string }) {
  return (
    <div className="p-4" data-testid={testId}>
      <div className="text-sm text-kumo-subtle">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
      {hint && <div className="text-xs text-kumo-inactive">{hint}</div>}
    </div>
  );
}

function Card({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-kumo-hairline bg-kumo-base p-5">
      <h3 className="m-0 text-base font-semibold">{title}</h3>
      {description && <div className="mb-3 text-sm text-kumo-subtle">{description}</div>}
      <div className={description ? '' : 'mt-3'}>{children}</div>
    </section>
  );
}

/** Observabilidade da organização: uso, custo, latência e turnos de todos os agentes (Analytics Engine + traces) */
export function ObservabilidadePage() {
  const fetchApi = useAuthenticatedFetch();
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState('');
  const [data, setData] = useState<Overview | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setData(null); setFailed(false);
    const q = new URLSearchParams({ days: String(days) });
    if (filter) q.set('filter', filter);
    fetchApi(`/api/ai/observability?${q}`).then((r) => (r.ok ? r.json() : Promise.reject(new Error('http')))).then((j) => { if (alive) setData(j); }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [days, filter, fetchApi]);

  const names = useMemo(() => new Map((data?.agents ?? []).map((a) => [a.id, a.name])), [data]);
  const usage = data?.usage;
  const dark = document.documentElement.dataset.mode === 'dark';

  const volume = useMemo((): ChartOptions | null => {
    if (!usage?.available || !usage.days.length) return null;
    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0 },
      grid: { left: 44, right: 16, top: 16, bottom: 40 },
      xAxis: { type: 'category', data: usage.days.map((d) => dayLabel(d.day)) },
      yAxis: { type: 'value', minInterval: 1 },
      series: [
        { name: 'Turnos', type: 'bar', data: usage.days.map((d) => d.turns) },
        { name: 'Erros', type: 'line', smooth: true, data: usage.days.map((d) => d.errors) },
        { name: 'Modelo de reserva', type: 'line', smooth: true, data: usage.days.map((d) => d.fallbacks) },
      ],
    };
  }, [usage]);
  const latency = useMemo((): ChartOptions | null => {
    if (!usage?.available || !usage.days.length) return null;
    return {
      tooltip: { trigger: 'axis', valueFormatter: (v: unknown) => `${(Number(v) / 1000).toFixed(1)} s` },
      legend: { bottom: 0 },
      grid: { left: 44, right: 16, top: 16, bottom: 40 },
      xAxis: { type: 'category', data: usage.days.map((d) => dayLabel(d.day)) },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `${v / 1000} s` } },
      series: [
        { name: '1ª palavra (p95)', type: 'line', smooth: true, data: usage.days.map((d) => d.p95FirstMs) },
        { name: 'Resposta completa (p95)', type: 'line', smooth: true, data: usage.days.map((d) => d.p95Ms) },
      ],
    };
  }, [usage]);
  const tokens = useMemo((): ChartOptions | null => {
    if (!usage?.available || !usage.days.length) return null;
    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0 },
      grid: { left: 56, right: 16, top: 16, bottom: 40 },
      xAxis: { type: 'category', data: usage.days.map((d) => dayLabel(d.day)) },
      yAxis: { type: 'value' },
      series: [
        { name: 'Entrada', type: 'bar', stack: 't', data: usage.days.map((d) => d.tokensIn) },
        { name: 'Saída', type: 'bar', stack: 't', data: usage.days.map((d) => d.tokensOut) },
      ],
    };
  }, [usage]);

  const t = usage?.available ? usage.totals : null;
  const empty = <div className="py-6 text-center text-kumo-inactive">Sem dados no período. Os pontos aparecem quando um agente publicado responde pelo runtime.</div>;

  return (
    <PageLayout
      title="Observabilidade"
      description="Uso, custo, latência e erros de todos os agentes da organização. Os turnos vêm do runtime; os números por dia, do Analytics Engine."
      actions={<Select value={String(days)} items={PERIODS} onValueChange={(v: string | null) => { if (v) setDays(Number(v)); }} aria-label="Período" data-testid="obs-periodo" />}
    >
      <div className="flex flex-col gap-5" data-testid="observabilidade">
        {failed && <Banner variant="error" description={<>Não foi possível carregar a observabilidade.</>} />}
        {usage && !usage.available && <Banner variant="default" description={<>Uso por dia indisponível: {usage.message}</>} />}

        <div className="grid overflow-hidden rounded-xl border border-kumo-hairline bg-kumo-base" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
          <Tile testId="obs-agentes" label="Agentes" value={data ? n(data.agents.length) : '—'} hint={usage?.available ? `${n(usage.byAgent.length)} com atividade` : undefined} />
          <Tile testId="obs-turnos" label="Turnos" value={t ? n(t.turns) : '—'} hint={t ? `${n(t.tools)} ferramentas usadas` : undefined} />
          <Tile label="Tokens" value={t ? n(t.tokensIn + t.tokensOut) : '—'} hint={t ? `${n(t.tokensIn)} entrada · ${n(t.tokensOut)} saída` : undefined} />
          <Tile label="Custo estimado" value={t ? usd(t.costMicroUsd) : '—'} hint={t?.turns ? `${usd(Math.round(t.costMicroUsd / t.turns))} por turno` : undefined} />
          <Tile label="1ª palavra (p95)" value={t ? secs(t.p95FirstMs) : '—'} hint="meta: até 3 s" />
          <Tile label="Taxa de erro" value={t ? pct(t.errors, t.turns) : '—'} hint={t ? `${n(t.errors)} falhas · ${n(t.fallbacks)} com reserva` : undefined} />
        </div>

        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))' }}>
          <Card title="Turnos e erros" description="Por dia">
            {!data ? <SkeletonText lines={5} label="Carregando" /> : volume ? <Chart echarts={echarts} height={240} isDarkMode={dark} options={volume} /> : empty}
          </Card>
          <Card title="Latência" description="Tempo até a primeira palavra e resposta completa, p95 por dia">
            {!data ? <SkeletonText lines={5} label="Carregando" /> : latency ? <Chart echarts={echarts} height={240} isDarkMode={dark} options={latency} /> : empty}
          </Card>
          <Card title="Tokens" description="Entrada e saída por dia">
            {!data ? <SkeletonText lines={5} label="Carregando" /> : tokens ? <Chart echarts={echarts} height={240} isDarkMode={dark} options={tokens} /> : empty}
          </Card>
        </div>

        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))' }}>
          <Card title="Por agente">
            <DataTable
              items={usage?.available ? usage.byAgent : []}
              loading={!data}
              loadingText="Carregando…"
              trackBy={(r: Keyed) => r.key}
              empty={empty}
              ariaLabels={{ tableLabel: 'Uso por agente' }}
              columnDefinitions={[
                { id: 'agente', header: 'Agente', cell: (r: Keyed) => names.get(r.key) ?? r.key.slice(0, 8) },
                { id: 'turnos', header: 'Turnos', cell: (r: Keyed) => n(r.turns) },
                { id: 'erro', header: 'Erro', cell: (r: Keyed) => pct(r.errors, r.turns) },
                { id: 'p95', header: '1ª palavra (p95)', cell: (r: Keyed) => secs(r.p95FirstMs) },
                { id: 'custo', header: 'Custo', cell: (r: Keyed) => usd(r.costMicroUsd) },
              ]}
            />
          </Card>
          <Card title="Por modelo">
            <DataTable
              items={usage?.available ? usage.byModel : []}
              loading={!data}
              loadingText="Carregando…"
              trackBy={(r: Keyed) => r.key}
              empty={empty}
              ariaLabels={{ tableLabel: 'Uso por modelo' }}
              columnDefinitions={[
                { id: 'modelo', header: 'Modelo', cell: (r: Keyed) => <code className="text-xs">{r.key || '—'}</code> },
                { id: 'turnos', header: 'Turnos', cell: (r: Keyed) => n(r.turns) },
                { id: 'tokens', header: 'Tokens', cell: (r: Keyed) => n(r.tokensIn + r.tokensOut) },
                { id: 'custo', header: 'Custo', cell: (r: Keyed) => usd(r.costMicroUsd) },
              ]}
            />
          </Card>
        </div>

        <Card title="Turnos recentes" description="Clique num turno para ver o trace: modelo, tokens, custo e ferramentas na ordem">
          <div className="mb-3 max-w-xs">
            <Select value={filter} items={FILTERS} onValueChange={(v: string | null) => setFilter(v ?? '')} aria-label="Filtro" />
          </div>
          {!data && <SkeletonText lines={5} label="Carregando" />}
          {data?.turns.length === 0 && <div className="text-kumo-inactive">Nenhum turno registrado ainda.</div>}
          {data && data.turns.length > 0 && (
            <ul className="m-0 list-none p-0" data-testid="obs-turnos-lista">
              {data.turns.map((turn) => (
                <li key={turn.id} className="border-t border-kumo-hairline">
                  <button type="button" onClick={() => setOpen(open === turn.id ? null : turn.id)} aria-expanded={open === turn.id} className="block w-full cursor-pointer border-0 bg-transparent px-0 py-2 text-left text-sm" style={{ color: 'inherit', font: 'inherit' }}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{turn.input || '—'}</span>
                      <Status s={turn.status} />
                    </div>
                    <div className="text-xs text-kumo-inactive">
                      {names.get(turn.agentId ?? '') ?? 'Agente removido'} · {new Date(turn.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} · {secs(turn.ms)} · {n(turn.tokensIn + turn.tokensOut)} tokens · {usd(turn.costMicroUsd)}
                    </div>
                  </button>
                  {open === turn.id && turn.agentId && <Detail agentId={turn.agentId} runId={turn.id} />}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </PageLayout>
  );
}
